import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';

import { CatsRecordTypeEnum } from '@shared-enums/cats.enum';
import { TimesheetStatusEnum } from '@shared-enums/time.enum';
import { trimLeadingZeros } from '@shared-helpers/string-number-helpers';
import { IExtDocument } from '@shared-interfaces/documents';

import { environment } from '../../../environments/environment';
import { ICatRecord } from '../../interfaces/incoming';
import { IAnyCatsType, ISelectOptions, ITableConfigEntity } from '../../interfaces/internal-interfaces';
import { IAnyInterfaceType } from '../../scripts/basicInterface';
import { BillingTimesheet } from '../../scripts/BillingTimesheet';
import { CatsRecord } from '../../scripts/catsRecord';
import { DetailPayneHandler, IDayOfWeekButtonEntity, IGroupedTable } from '../../scripts/detailPayneHandler';
import { Employee } from '../../scripts/employee';
import { EmployeeCatsRecord } from '../../scripts/employeeCatsRecord';
import { Equipment } from '../../scripts/equipment';
import { ExtDocument } from '../../scripts/extDocument';
import { NonbillableCatsRecord } from '../../scripts/NonbillableCatsRecord';
import { DatahubService } from '../../services/datahub.service';
import { LogRocketService } from '../../services/log-rocket.service';
import { MessageService } from '../../services/message.service';
import { ModalService } from '../../services/modal.service';
import { TimesheetHelperService } from '../../services/timesheet-helper.service';
import { ISplitRecordResult } from '../modal/modal-content-split-modal/modal-content-split-modal.component';
import { IModalContentCreateOrEditTimesheetResult } from '../modal/modal-content-timesheet-create-or-edit/modal-content-timesheet-create-or-edit.component';
import { ITableLikeLayoutEvent } from '../table-like-layout/table-like-layout.component';

@Component({
  selector: 'detail-payne',
  templateUrl: './detail-payne.component.html',
  styleUrl: './detail-payne.component.css',
  standalone: false,
})
export class DetailPayneComponent implements OnInit {
  public detailPayneHandler: DetailPayneHandler;
  public dayButtons = this.datahub.detailPayneHandler.dayButtons;
  public approvalName = '';

  constructor(
    private readonly destroyRef: DestroyRef,
    private readonly datahub: DatahubService,
    private readonly modalService: ModalService,
    private readonly timesheetHelperSvc: TimesheetHelperService,
    private readonly messageSvc: MessageService,
    private readonly logRocket: LogRocketService,
  ) {}

  public ngOnInit(): void {
    this.detailPayneHandler = this.datahub.detailPayneHandler;

    this.approvalName = this.isWorkingAsProxyApprover ? this.datahub.user.approvingName : 'you';

    this.timesheetHelperSvc.isWorking$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (isWorking: boolean) => {
        this.detailPayneHandler.isWorking = isWorking;
      },
      error: (err) => {
        console.error('Error occurred while updating isWorking state:', err);
        this.datahub.messageService.error('There was an issue updating the working status.');
      },
    });
  }

  private get isWorkingAsProxyApprover(): boolean {
    return this.datahub.user.getEmpId() !== this.datahub.user.approvingId;
  }

  private handleAddBtnClicked(record: IAnyCatsType, group: IGroupedTable): void {
    const blankRecord = this.detailPayneHandler.handleAddBtnClicked(record.Extdocumentno, group.config);
    group.values.push(blankRecord);
  }

  public handleAddNonbillClicked(existingRecord?: NonbillableCatsRecord): void {
    const employee = this.detailPayneHandler.focusedInterFace as Employee;

    if (this.datahub.user.isDirectReport(employee.EmpId)) {
      this.datahub.payrollTimesheet.setNonbillTimesheet(
        this.datahub.user.approvingId,
        this.datahub.payrollTimesheet.extDocuments,
      );

      const modalData = {
        absence: this.datahub.nonBillable.absence,
        attendance: this.datahub.nonBillable.attendance,
        workScheduleRule: employee.WorkScheduleRule,
        focusedDay: this.detailPayneHandler.focusedInterFace?.focusedDay,
      };

      this.modalService
        .launchResponsiveModal('nonbillable-select', modalData)
        .afterClosed()
        .pipe(filter((val) => val != null))
        .subscribe({
          next: (x: ISelectOptions) => {
            if (existingRecord) {
              // Modify existing record (click on button in a row)
              existingRecord.updateProperty('AbsAttType', x.value);
              this.detailPayneHandler.isSaveBtnDisabled = false;
            } else {
              // Create new record (click on "Add Nonbillable" button)
              this.datahub.detailPayneHandler.handleNonbillClicked(x.value);
            }
          },
          error: (err) => {
            console.error('Error occurred while adding nonbillable record:', err);
            this.datahub.messageService.error('There was an error while processing the nonbillable record.');
          },
        });
    } else {
      this.datahub.messageService.warning(
        "You cannot add nonbillable for an employee that doesn't report to you. Please contact this employee's manager to add nonbill.",
      );
    }
  }

  private handleDeleteClicked(element: CatsRecord, group: IGroupedTable, anyInterface: IAnyInterfaceType): void {
    anyInterface.wasDeleteAttempted = true;
    element.shouldShow = false;
    element.__shouldDelete = true;
    group.isHidden = true;
    group.values.map((x) => {
      if (x.shouldShow) group.isHidden = false;
    });
    this.detailPayneHandler.isSaveBtnDisabled = false;
  }

  public handleIconClicked(eventValue: ITableLikeLayoutEvent<IAnyCatsType>, group: IGroupedTable): void {
    const clickValue = eventValue.columnConfig.clickValue ?? 'UnknownIconClicked';
    const entity = eventValue.entity;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-unnecessary-type-assertion -- We know we have a focused interface since we just clicked on icon in details pane
    const focusedInterFace = this.detailPayneHandler.focusedInterFace!;
    switch (clickValue) {
      case 'showCats':
        this.showCats(entity);
        break;
      case 'deleteClicked':
        this.handleDeleteClicked(entity, group, focusedInterFace);
        break;
      case 'addClicked':
        this.handleAddBtnClicked(entity, group);
        break;
      case 'splitClicked':
        this.handleSplitClicked(entity as EmployeeCatsRecord, group);
        break;
      case 'moveTimeSheetClicked':
        this.handleMoveTimeSheetClicked(entity);
        break;
      case 'navigateToTor':
        this.navigateToTor();
        break;
      default:
        console.info(`Click value '${clickValue}' is not being handled here!`);
        break;
    }

    this.logRocket.track(clickValue, {
      //Real User
      employeeNumber: this.datahub.user.getEmpId(),
      userName: this.datahub.user.userName,
      costCenter: this.datahub.user.getHomeCC(),
      //Working As User
      workingAsEmployeeNumber: this.datahub.user.approvingId,
      workingAsUserName: this.datahub.user.approvingName,
      workingAsCostCenter: this.datahub.user.approvingCostCenter,
    });
  }

  public handlePlainButtonClicked(eventValue: ITableLikeLayoutEvent<NonbillableCatsRecord>): void {
    const clickValue = eventValue.columnConfig.clickValue;

    if (clickValue === 'nonbillableCodeClicked') {
      this.handleAddNonbillClicked(eventValue.entity);
    }
  }

  private navigateToTor(): void {
    window.open(environment.torUrl, '_blank');
  }

  private showCats(row: CatsRecord): void {
    this.modalService.launchResponsiveModal('cats-info', row);
  }

  public handleValueChange(event: ITableLikeLayoutEvent<any>): void {
    //We have to re-cast the type here or else the HTML template will throw type errors since the table doesn't understand the types we expect here
    const typedEvent = event as ITableLikeLayoutEvent<
      NonbillableCatsRecord | EmployeeCatsRecord,
      keyof (NonbillableCatsRecord | EmployeeCatsRecord)
    >;
    if (typedEvent.entity.Zrecordtype === CatsRecordTypeEnum.EmployeePerDiem) {
      const perDiemConfig = this.detailPayneHandler.groupedTable.find((x) => x.type === 'perDiem');
      if (perDiemConfig != null) perDiemConfig.headerText = this.detailPayneHandler.getPerDiemTotals(perDiemConfig.values);
    }
    const value$ = typedEvent.entity[typedEvent.columnConfig.key];
    typedEvent.entity.updateProperty(typedEvent.columnConfig.key, value$);
    this.detailPayneHandler.handleDisableRecordFeatures(typedEvent.entity.__config);
    if (this.detailPayneHandler.canBulkAdd) {
      //if bulk adding, don't enable the save button until a day has been selected
      this.detailPayneHandler.isSaveBtnDisabled = !this.dayButtons.some((butt) => butt.isSelected);
    } else {
      this.detailPayneHandler.isSaveBtnDisabled = false;
    }
  }

  private handleSplitClicked(parentRecord: EmployeeCatsRecord, group: IGroupedTable): void {
    const data = { pool: parentRecord.Catshours };
    this.modalService
      .launchResponsiveModal('split', data, { autoFocus: 'first-tabbable' })
      .afterClosed()
      .subscribe({
        next: (results: ISplitRecordResult) => {
          if (!results) return; // Do nothing when the backdrop is clicked, assumed canceled.
          if (!results.shouldSplit) return; // Do nothing if cancel is clicked
          const splitOffRecord = parentRecord.splitSelf(results.splitValue);
          group.values.push(splitOffRecord);
          this.detailPayneHandler.isSaveBtnDisabled = false;
          this.detailPayneHandler.handleDisableRecordFeatures(parentRecord.__config);
          this.detailPayneHandler.handleDisableRecordFeatures(splitOffRecord.__config);
        },
        error: (err) => {
          console.error('Error occurred during split operation:', err);
          this.messageSvc.error('There was an error processing the split operation.');
        },
      });
  }

  private handleMoveTimeSheetClicked(recordToMove: IAnyCatsType): void {
    const billingTimesheet = this.datahub.billingTimesheet;
    this.modalService
      .launchResponsiveModal('timesheet-move', { record: recordToMove, timesheet: billingTimesheet })
      .afterClosed()
      .pipe(filter((x) => x)) // Remove empty, happens when modal is closed without selecting a place to move it to
      .subscribe({
        next: (result: IModalContentCreateOrEditTimesheetResult | ExtDocument) => {
          if (result instanceof ExtDocument) {
            // Move the record to an existing document
            this.handleMoveRecord(billingTimesheet, result, recordToMove);
          } else if (Array.isArray(result.headers)) {
            // Create a new document and then move the record there
            this.handleCreateTimesheetAndMoveRecord(billingTimesheet, result, recordToMove);
          } else {
            // Shouldn't happen, but just in case!
            this.messageSvc.error('Unable to move this record because there was a problem finding where to move it to');
          }
        },
        error: (err) => {
          console.error('Error occurred while moving the timesheet:', err);
          this.messageSvc.error('There was an error moving the timesheet.');
        },
      });
  }

  public handleAddTimesheetClicked(): void {
    this.datahub.detailPayneHandler.handleAddDocumentClicked();
  }

  public handleBulkAddDayButtonChange(button: IDayOfWeekButtonEntity): void {
    button.isSelected = !button.isSelected;
    this.detailPayneHandler.isSaveBtnDisabled = !this.dayButtons.some((butt) => butt.isSelected); //only allow bulk-saving if at least one button is selected
  }

  public handleSaveClicked(): void {
    this.detailPayneHandler.isWorking = true;
    const focusedInterface = this.detailPayneHandler.focusedInterFace as Employee | Equipment;
    const selected = this.dayButtons.filter((x) => x.isSelected);
    this.detailPayneHandler.groupedTable.forEach((x) => {
      const { hasError, message } = this.detectRequired(x.config, x.values);
      if (hasError) {
        this.datahub.messageService.error(message);
      }
    });
    this.datahub.detailPayneHandler.save$(focusedInterface, selected).subscribe({
      next: () => (this.detailPayneHandler.isWorking = false),
      error: () => (this.detailPayneHandler.isWorking = false),
      complete: () => {
        this.detailPayneHandler.isWorking = false;
        this.dayButtons.forEach((dayButton) => (dayButton.isSelected = false));
      },
    });
  }

  public handleBillCodeMappingClicked(): void {
    const focusedInterFace = this.detailPayneHandler.focusedInterFace as Employee | Equipment;
    const type = focusedInterFace.billCodeType;
    const billingTimesheet = this.detailPayneHandler.focusedTimesheet as BillingTimesheet;
    const order = billingTimesheet.contractWrapper;
    this.detailPayneHandler.handleDefaultClicked$(focusedInterFace, type, order).subscribe({
      error: (err) => {
        console.error(err);
      },
    });
  }

  private detectRequired(
    config: ITableConfigEntity<keyof CatsRecord>[],
    values: CatsRecord[],
  ): { hasError: boolean; message: string } {
    const required: ITableConfigEntity<keyof CatsRecord>[] = config.filter((x) => x.isRequired);
    let hasError = false;
    let message = '';
    values.forEach((record) => {
      if (hasError) return;
      if (!record.__isModified || !record.shouldShow) return;
      required.forEach((property) => {
        if (hasError) return;
        if (!record[property.key]) {
          message = `${property.header} is required.`;
          hasError = true;
        }
      });
    });
    return { hasError: hasError, message };
  }

  public isString(toCheck: string | string[]): toCheck is string {
    return typeof toCheck === 'string';
  }

  private handleMoveRecord(
    billingTimesheet: BillingTimesheet,
    targetDocument: ExtDocument,
    recordToMove: IAnyCatsType,
    newlyCreatedDocument?: ExtDocument,
  ) {
    const prevDocumentNo = recordToMove.Extdocumentno;
    this.timesheetHelperSvc.moveSingleRecord$(billingTimesheet, targetDocument, recordToMove).subscribe({
      next: (updatedRecord) => {
        this.handleUpdateViewAfterRecordMove(updatedRecord, prevDocumentNo, newlyCreatedDocument);
      },
      error: (err: unknown) => {
        const msg =
          newlyCreatedDocument instanceof ExtDocument
            ? `The new timesheet #${trimLeadingZeros(newlyCreatedDocument.Documentno)} was created, but there was an error moving the selected record into it!`
            : `There was an error moving the record to the selected timesheet`;
        this.messageSvc.error(msg);
        console.error(err);
      },
    });
  }

  private handleCreateTimesheetAndMoveRecord(
    billingTimesheet: BillingTimesheet,
    newTimesheetResult: IModalContentCreateOrEditTimesheetResult,
    recordToMove: IAnyCatsType,
  ): void {
    this.timesheetHelperSvc
      .create$(
        billingTimesheet.contractWrapper,
        newTimesheetResult.description,
        newTimesheetResult.headers,
        TimesheetStatusEnum.PayrollApproved,
      )
      .subscribe({
        next: (createdTimesheet: IExtDocument) => {
          const newDocument = new ExtDocument(createdTimesheet, this.datahub, false);
          billingTimesheet.addExtDoc(newDocument);
          this.handleMoveRecord(billingTimesheet, newDocument, recordToMove, newDocument);
        },
        error: (err: unknown) => {
          this.messageSvc.error(`There was an error creating the new timesheet`);
          console.error(err);
        },
      });
  }

  public handleUpdateViewAfterRecordMove(updatedRecord: ICatRecord, prevDocNo: string, newlyCreatedDocument?: ExtDocument) {
    //When we get a response back after the modal has closed, the data has been moved but we need to update the UI to match
    const found = this.detailPayneHandler.focusedInterFace?.catsRecords.find(
      (cat) => cat.Counter === updatedRecord.Counter || cat.Counter === updatedRecord.Refcounter,
    );
    if (updatedRecord.Extdocumentno === prevDocNo) {
      const msg =
        newlyCreatedDocument instanceof ExtDocument
          ? `The new timesheet <strong>#${trimLeadingZeros(newlyCreatedDocument.Documentno)}</strong> was created, but the selected record was not able to be moved to it!`
          : `The selected record was not able to be moved!`;
      this.messageSvc.error(msg, 'Record Not Moved');
    } else if (found) {
      found.Status = updatedRecord.Status; //records are not coming back from the modify function in status 20.
      found.Extdocumentno = updatedRecord.Extdocumentno;
      found.Counter = updatedRecord.Counter;
      found.Refcounter = updatedRecord.Refcounter;
      this.detailPayneHandler.focusedInterFace?.checkApprovalStatus([updatedRecord]);
      this.detailPayneHandler.showSummary(); //unfocus the details pane from the record that is no longer here
      const msg =
        newlyCreatedDocument instanceof ExtDocument
          ? `The new timesheet <strong>#${trimLeadingZeros(newlyCreatedDocument.Documentno)}</strong> was created, and the selected record has been moved to it`
          : `The selected record has been moved to the selected timesheet`;
      this.messageSvc.success(msg, 'Record Moved');
    } else {
      //This shouldn't really happen... but just in case
      this.messageSvc.error(
        `The selected record has been moved out of it's original timesheet but it cannot be located in the current view! Please refresh the page.`,
        'Unable to find moved record!',
      );
      this.detailPayneHandler.showSummary(); //unfocus the details pane from the record that is no longer here
    }
  }
}
